/**
 * SCR-017 — Chi tiết báo cáo tiến độ.
 * Sections: A (nội dung) · B (share links) · C (comments/feedback).
 * Export PDF/Excel: client-side stub (FR-C3-15 full impl Phase 07).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
  ArrowLeft, Pencil, Share2, Copy, Ban, MessageSquare, Download, ChevronDown,
} from 'lucide-react';
import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ReportForm, type ReportFormValues } from '@client/components/forms/report-form';
import { SessionReviewStatsCard } from '@client/components/report/session-review-stats-card';
import { isApiError } from '@client/lib/queries/use-branches';
import {
  useProgressReport, progressReportKeys,
} from '@client/lib/queries/use-progress-reports';
import { useShareLinks, useCreateShareLink, useRevokeShareLink } from '@client/lib/queries/use-share-links';
import { useReportComments, useCreateReportComment } from '@client/lib/queries/use-report-comments';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/admin/reports/$reportId')({
  component: ReportDetailPage,
});

const STATUS_META = {
  draft: { label: 'Nháp', className: 'bg-secondary text-muted-foreground' },
  published: { label: 'Đã công bố', className: 'bg-brand-green-100 text-brand-green-700' },
};

const RATING_LABELS: Record<string, string> = {
  excellent: 'Xuất sắc', good: 'Tốt', average: 'Trung bình', needs_improvement: 'Cần cải thiện',
};

function ReportDetailPage() {
  const { reportId } = Route.useParams();
  const qc = useQueryClient();

  const { data: report, isLoading, isError, refetch } = useProgressReport(reportId);

  // Section B — share links
  const { data: shareLinksData, refetch: refetchLinks } = useShareLinks(reportId);
  const createLink = useCreateShareLink(reportId);
  const revokeLink = useRevokeShareLink(reportId);

  // Section C — comments
  const { data: commentsData } = useReportComments(reportId);
  const createComment = useCreateReportComment(reportId);

  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [expiresAt, setExpiresAt] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [shareLinkError, setShareLinkError] = useState<string | null>(null);

  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [isSendingComment, setIsSendingComment] = useState(false);

  const commentInputRef = useRef<HTMLTextAreaElement>(null);

  const shareLinks = shareLinksData?.items ?? [];
  const comments = commentsData?.items ?? [];

  // 404 redirect
  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/admin/reports" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground w-fit">
          <ArrowLeft className="h-4 w-4" /> Quay lại
        </Link>
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>Không tải được báo cáo hoặc báo cáo không tồn tại.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>Thử lại</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading || !report) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const statusMeta = STATUS_META[report.status];
  const isPublished = report.status === 'published';

  async function handleTogglePublish() {
    if (isPublished) {
      const activeLinks = shareLinks.filter((l) => !l.revoked);
      if (activeLinks.length > 0 && !confirm('Phụ huynh sẽ không xem được qua link đã chia sẻ. Tiếp tục thu hồi công bố?')) return;
    }
    try {
      await api.patch(`/api/progress-reports/${reportId}`, {
        status: isPublished ? 'draft' : 'published',
      });
      await qc.invalidateQueries({ queryKey: progressReportKeys.all });
      toast.success(isPublished ? 'Đã thu hồi công bố.' : 'Đã công bố báo cáo.');
    } catch (err) {
      toast.error(isApiError(err) ? err.message : 'Lỗi khi cập nhật trạng thái.');
    }
  }

  async function handleEditSubmit(values: ReportFormValues) {
    setEditError(null); setIsEditSubmitting(true);
    try {
      await api.patch(`/api/progress-reports/${reportId}`, values);
      await qc.invalidateQueries({ queryKey: progressReportKeys.all });
      toast.success('Đã cập nhật báo cáo.');
      setEditOpen(false);
    } catch (err) {
      setEditError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
    } finally { setIsEditSubmitting(false); }
  }

  async function handleCreateShareLink() {
    const today = new Date().toISOString().slice(0, 10);
    if (expiresAt <= today) { setShareLinkError('Hạn phải sau hôm nay.'); return; }
    setShareLinkError(null);
    try {
      await createLink.mutateAsync({ expiresAt: `${expiresAt}T23:59:59.000Z` });
      toast.success('Đã tạo link chia sẻ.');
      setShareLinkOpen(false);
      void refetchLinks();
    } catch (err) {
      setShareLinkError(isApiError(err) ? err.message : 'Lỗi khi tạo link chia sẻ.');
    }
  }

  async function handleRevoke(linkId: string) {
    if (!confirm('Thu hồi link chia sẻ này? Link sẽ không truy cập được nữa.')) return;
    try {
      await revokeLink.mutateAsync(linkId);
      toast.success('Đã thu hồi link.');
    } catch (err) {
      toast.error(isApiError(err) ? err.message : 'Lỗi khi thu hồi link.');
    }
  }

  function handleCopyUrl(token: string, url?: string) {
    const copyUrl = url ?? `${window.location.origin}/r/${token}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(copyUrl)
        .then(() => toast.success('Đã sao chép link.'))
        .catch(() => toast.info(`Link: ${copyUrl}`));
    } else {
      toast.info(`Link: ${copyUrl}`);
    }
  }

  function handleExport(type: 'pdf' | 'excel') {
    // FR-C3-15 stub — full client-side export implemented in Phase 07
    toast.info(`Export ${type.toUpperCase()} sẽ được hỗ trợ đầy đủ ở Phase 07.`);
  }

  async function handleSendComment() {
    const content = commentText.trim();
    if (!content) { setCommentError('Nội dung không được để trống.'); return; }
    setCommentError(null); setIsSendingComment(true);
    try {
      await createComment.mutateAsync({ content });
      setCommentText('');
      toast.success('Đã gửi phản hồi.');
    } catch (err) {
      setCommentError(isApiError(err) ? err.message : 'Lỗi khi gửi phản hồi.');
    } finally { setIsSendingComment(false); }
  }

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <Link to="/admin/reports" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground w-fit">
          <ArrowLeft className="h-4 w-4" /> Quay lại danh sách
        </Link>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold">
              Báo cáo: {report.studentName ?? 'HV'} — {report.className ?? 'Lớp'}
            </h1>
            <Badge className={`${statusMeta.className} border-0`}>{statusMeta.label}</Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { setEditError(null); setEditOpen(true); }}>
              <Pencil className="h-4 w-4 mr-1" /> Sửa
            </Button>
            <Button
              variant={isPublished ? 'outline' : 'default'}
              size="sm"
              onClick={() => void handleTogglePublish()}
            >
              {isPublished ? 'Thu hồi công bố' : 'Công bố'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!report}>
                  <Download className="h-4 w-4 mr-1" /> Export <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')}>PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')}>Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Section A — Report content */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Nội dung báo cáo</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <ReportField label="Kỳ" value={`${report.period}${report.periodLabel ? ` — ${report.periodLabel}` : ''}`} />
            <ReportField label="Đánh giá" value={report.overallRating ? RATING_LABELS[report.overallRating] : undefined} />
            <ReportField label="Từ ngày" value={report.periodStart ? format(new Date(report.periodStart), 'dd/MM/yyyy') : undefined} />
            <ReportField label="Đến ngày" value={report.periodEnd ? format(new Date(report.periodEnd), 'dd/MM/yyyy') : undefined} />
            <div className="md:col-span-2">
              <ReportField label="Tóm tắt điểm danh" value={report.attendanceSummary} />
            </div>
            <div className="md:col-span-2">
              <ReportField label="Nhận xét học lực" value={report.academicComment} />
            </div>
            <div className="md:col-span-2">
              <ReportField label="Nhận xét hành vi" value={report.behaviorComment} />
            </div>
            <div className="md:col-span-2">
              <ReportField label="Khuyến nghị" value={report.recommendation} />
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Session review aggregate stats */}
      {report.sessionReviewStats && (
        <SessionReviewStatsCard stats={report.sessionReviewStats} />
      )}

      {/* Section B — Share links */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Share2 className="h-4 w-4" /> Chia sẻ
          </CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="sm"
                  disabled={!isPublished}
                  onClick={() => { setShareLinkError(null); setShareLinkOpen(true); }}
                >
                  Tạo link chia sẻ
                </Button>
              </span>
            </TooltipTrigger>
            {!isPublished && (
              <TooltipContent>Cần công bố báo cáo trước khi tạo link</TooltipContent>
            )}
          </Tooltip>
        </CardHeader>
        <CardContent>
          {shareLinks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có link chia sẻ nào.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {shareLinks.map((link) => {
                const linkUrl = link.url ?? `${window.location.origin}/r/${link.token}`;
                return (
                  <div key={link.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-md border border-border text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs break-all text-muted-foreground">{linkUrl}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Hạn: {format(new Date(link.expiresAt), 'dd/MM/yyyy')}
                        {link.revoked && <span className="ml-2 text-destructive font-medium">· Đã thu hồi</span>}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline" size="sm" className="h-9 min-w-[44px]"
                        onClick={() => handleCopyUrl(link.token, link.url)}
                        disabled={link.revoked}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                      </Button>
                      <Button
                        variant="outline" size="sm" className="h-9 min-w-[44px] text-destructive hover:text-destructive"
                        onClick={() => void handleRevoke(link.id)}
                        disabled={link.revoked}
                      >
                        <Ban className="h-3.5 w-3.5 mr-1" /> Thu hồi
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section C — Comments */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Phản hồi
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có phản hồi nào.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {comments.map((c) => (
                <div key={c.id} className="flex flex-col gap-0.5 p-3 rounded-md bg-muted/50 text-sm">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{c.authorName ?? 'Ẩn danh'}</span>
                    <span>·</span>
                    <span>{format(new Date(c.createdAt), 'dd/MM/yyyy HH:mm')}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap">{c.content}</p>
                </div>
              ))}
            </div>
          )}
          {/* Comment input */}
          <div className="flex flex-col gap-2 pt-2 border-t border-border">
            <Textarea
              ref={commentInputRef}
              rows={3}
              maxLength={1000}
              placeholder="Nhập phản hồi..."
              value={commentText}
              onChange={(e) => { setCommentText(e.target.value); setCommentError(null); }}
              className="resize-none"
            />
            {commentError && <p className="text-xs text-destructive">{commentError}</p>}
            <Button
              size="sm"
              className="self-end h-10 min-w-[80px]"
              disabled={isSendingComment || !commentText.trim()}
              onClick={() => void handleSendComment()}
            >
              {isSendingComment ? 'Đang gửi...' : 'Gửi'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sửa báo cáo</DialogTitle></DialogHeader>
          {editError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{editError}</AlertDescription>
            </Alert>
          )}
          <ReportForm
            isEdit
            isSubmitting={isEditSubmitting}
            defaultValues={{
              period: report.period,
              periodLabel: report.periodLabel ?? '',
              periodStart: report.periodStart ?? '',
              periodEnd: report.periodEnd ?? '',
              attendanceSummary: report.attendanceSummary ?? '',
              academicComment: report.academicComment ?? '',
              behaviorComment: report.behaviorComment ?? '',
              recommendation: report.recommendation ?? '',
              overallRating: report.overallRating ?? '',
              status: report.status,
            }}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Create share link dialog */}
      <Dialog open={shareLinkOpen} onOpenChange={setShareLinkOpen}>
        <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle>Tạo link chia sẻ</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expires-at">Ngày hết hạn <span className="text-destructive">*</span></Label>
              <Input
                id="expires-at"
                type="date"
                value={expiresAt}
                onChange={(e) => { setExpiresAt(e.target.value); setShareLinkError(null); }}
              />
              {shareLinkError && <p className="text-xs text-destructive">{shareLinkError}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShareLinkOpen(false)}>Hủy</Button>
              <Button onClick={() => void handleCreateShareLink()} disabled={createLink.isPending}>
                {createLink.isPending ? 'Đang tạo...' : 'Tạo link'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm whitespace-pre-wrap">{value ?? <span className="text-muted-foreground/60">—</span>}</dd>
    </div>
  );
}

